import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { roleLabel } from '../lib/format';

type NavEntry = { to: string; label: string; icon: string };

const workNav: NavEntry[] = [
  { to: '/dashboard', label: 'Overview', icon: 'dashboard' },
  { to: '/today', label: 'My day', icon: 'today' },
  { to: '/leads', label: 'Leads', icon: 'leads' },
  { to: '/projects', label: 'Projects', icon: 'projects' },
  { to: '/posts', label: 'Campaign Studio', icon: 'campaigns' },
  { to: '/agent', label: 'AI Sales Agent', icon: 'simulator' },
];
const manageNav: NavEntry[] = [
  { to: '/review', label: 'Approvals', icon: 'review' },
  { to: '/links', label: 'Tracked links', icon: 'links' },
  { to: '/money', label: 'Commissions', icon: 'money' },
  { to: '/team', label: 'Team', icon: 'team' },
  { to: '/simulator', label: 'AI simulator', icon: 'simulator' },
  { to: '/settings', label: 'Settings', icon: 'settings' },
];

// Small, consistent line icons keep navigation recognisable at phone sizes.
const iconPaths: Record<string, string[]> = {
  dashboard: ['M3 3h7v7H3z', 'M14 3h7v7h-7z', 'M3 14h7v7H3z', 'M14 14h7v7h-7z'],
  today: ['M12 7v5l3 2', 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20'],
  leads: ['M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2', 'M9.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8', 'M20 21v-2a4 4 0 0 0-3-3.9', 'M16 3.1a4 4 0 0 1 0 7.8'],
  projects: ['M3 21V5l9-3 9 3v16', 'M9 21v-6h6v6', 'M7 8h.01', 'M12 8h.01', 'M17 8h.01', 'M7 12h.01', 'M12 12h.01', 'M17 12h.01'],
  campaigns: ['M3 11v2a2 2 0 0 0 2 2h3l2 5h3l-2-5', 'M8 10l12-6v16L8 14z', 'M20 9a3 3 0 0 1 0 6'],
  review: ['M9 12l2 2 4-4', 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20'],
  links: ['M10 13a5 5 0 0 0 7.1 0l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1', 'M14 11a5 5 0 0 0-7.1 0l-2 2a5 5 0 0 0 7.1 7.1l1.1-1.1'],
  money: ['M3 7h18v14H3z', 'M3 7V4h16', 'M16 14h5', 'M16 14h.01'],
  team: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8', 'M22 21v-2a4 4 0 0 0-3-3.9', 'M16 3.1a4 4 0 0 1 0 7.8'],
  simulator: ['M9 2h6', 'M10 2v7l-6 9a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3l-6-9V2', 'M8 16h8'],
  settings: ['M4 7h16', 'M4 17h16', 'M8 4v6', 'M16 14v6'],
  more: ['M4 7h16', 'M4 12h16', 'M4 17h16'],
};

function NavIcon({ name }: { name: string }) {
  return <svg aria-hidden="true" viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {(iconPaths[name] ?? iconPaths.dashboard).map((d, i) => <path key={i} d={d} />)}
  </svg>;
}

function DesktopItem({ item }: { item: NavEntry }) {
  return <NavLink to={item.to} className={({ isActive }) => `group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-colors ${isActive ? 'bg-accentSoft text-accent' : 'text-muted hover:bg-surface2 hover:text-ink'}`}>
    <NavIcon name={item.icon} />{item.label}
  </NavLink>;
}

export function Shell() {
  const { me, firm, signOut, isManager } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  const canReview = isManager || me?.role === 'reviewer';
  const work = workNav.filter((n) => (n.to !== '/dashboard' && n.to !== '/agent') || isManager);
  const manage = manageNav.filter((n) => n.to === '/links' || n.to === '/team' || (n.to === '/review' ? canReview : isManager));
  const inMore = manage.some((n) => location.pathname.startsWith(n.to));
  const [moreOpen, setMoreOpen] = useState(inMore);
  useEffect(() => { if (inMore) setMoreOpen(true); }, [inMore]);
  const mobile = [
    { to: '/today', label: 'Today', icon: 'today' },
    { to: '/leads', label: 'Leads', icon: 'leads' },
    { to: '/projects', label: 'Projects', icon: 'projects' },
    { to: '/posts', label: 'Campaigns', icon: 'campaigns' },
  ];
  const moreSelected = !mobile.some((n) => location.pathname.startsWith(n.to));

  return <div className="min-h-screen lg:grid lg:grid-cols-[248px_minmax(0,1fr)]">
    <aside className="sticky top-0 hidden h-screen flex-col border-r border-line bg-white lg:flex">
      <div className="flex items-center gap-3 border-b border-line px-6 py-5">
        <div className="grid h-10 w-10 place-items-center rounded-2xl bg-[#193c60] text-lg font-extrabold tracking-tight text-white">R<span className="text-[#e4ac6f]">.</span></div>
        <div><p className="text-[19px] font-extrabold leading-tight tracking-[-.035em]">ReAstra</p><p className="text-[11px] font-semibold uppercase tracking-[.12em] text-muted">AI sales desk</p></div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6">
        <p className="section-eyebrow mb-3 px-3">Workspace</p>
        <nav className="space-y-1" aria-label="Main navigation">{work.map((n) => <DesktopItem key={n.to} item={n} />)}</nav>
        <div className="my-5 border-t border-line" />
        <details className="group" open={moreOpen} onToggle={(e) => setMoreOpen(e.currentTarget.open)}>
          <summary className={`flex cursor-pointer list-none items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-semibold hover:bg-surface2 ${inMore ? 'text-accent' : 'text-muted'}`}>
            <span className="flex items-center gap-3"><NavIcon name="more" />More tools</span>
            <span aria-hidden="true" className="text-[12px] transition-transform group-open:rotate-180">⌄</span>
          </summary>
          <nav className="mt-2 space-y-1 border-l border-line pl-3" aria-label="Management">{manage.map((n) => <DesktopItem key={n.to} item={n} />)}</nav>
        </details>
      </div>
      <div className="border-t border-line px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accentSoft text-sm font-extrabold text-accent">{(me?.full_name ?? 'R').trim().slice(0, 1).toUpperCase()}</div>
          <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold">{me?.full_name}</p><p className="text-xs text-muted">{me && roleLabel[me.role]}</p></div>
          <button onClick={signOut} className="text-xs font-semibold text-muted hover:text-accent">Exit</button>
        </div>
      </div>
    </aside>

    <div className="min-w-0">
      <header className="flex h-[68px] items-center justify-between border-b border-line bg-white px-5 sm:px-8">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[.12em] text-muted lg:hidden">ReAstra · AI sales desk</p>
          <p className="truncate text-[15px] font-bold text-ink lg:text-sm lg:font-semibold">{firm?.name ?? 'Your workspace'}</p>
        </div>
        <span className="hidden rounded-full border border-line bg-surface2 px-3 py-1.5 text-xs font-semibold text-muted sm:inline-flex">{me && roleLabel[me.role]}</span>
        <button onClick={() => setMenuOpen(true)} aria-label="Open navigation" className="rounded-xl border border-line p-2 text-muted lg:hidden"><NavIcon name="more" /></button>
      </header>
      <main className="mx-auto w-full max-w-[1280px] px-4 pb-28 pt-6 sm:px-7 lg:px-10 lg:pb-12 lg:pt-8"><Outlet /></main>
    </div>

    <nav aria-label="Phone navigation" className="fixed inset-x-0 bottom-0 z-40 flex h-[72px] items-stretch border-t border-line bg-white/95 shadow-[0_-8px_24px_rgba(31,57,89,.06)] backdrop-blur-lg lg:hidden">
      {mobile.map((n) => <NavLink key={n.to} to={n.to} className={({ isActive }) => `flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 text-[11px] font-semibold ${isActive ? 'text-accent' : 'text-muted'}`}><NavIcon name={n.icon} /><span>{n.label}</span></NavLink>)}
      <button aria-expanded={menuOpen} onClick={() => setMenuOpen(true)} className={`flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 text-[11px] font-semibold ${moreSelected ? 'text-accent' : 'text-muted'}`}><NavIcon name="more" />More</button>
    </nav>

    {menuOpen && <div className="fixed inset-0 z-50 bg-scrim lg:hidden" onMouseDown={() => setMenuOpen(false)}>
      <div role="dialog" aria-modal="true" aria-label="More navigation" className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-[28px] bg-white p-5 pb-8 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="mb-5 flex items-center justify-between"><p className="text-lg font-extrabold">Your workspace</p><button onClick={() => setMenuOpen(false)} aria-label="Close navigation" className="rounded-lg bg-surface2 px-3 py-1.5 text-sm font-bold">Close</button></div>
        <nav aria-label="All sections" className="grid grid-cols-2 gap-2">{[...work, ...manage].map((n) => <NavLink key={n.to} to={n.to} className="flex items-center gap-2.5 rounded-xl border border-line p-3 text-sm font-semibold text-ink"><NavIcon name={n.icon} />{n.label}</NavLink>)}</nav>
        <button onClick={signOut} className="mt-5 w-full rounded-xl border border-line p-3 text-sm font-semibold text-muted">Sign out</button>
      </div>
    </div>}
  </div>;
}
